/**
 * EVC Transaction Factory Utilities
 *
 * This module provides utilities for converting transaction items to EVC batch calls
 * using wagmi-evc's GeneralTransactionFactoryUnifier.
 */

import { whypeConfig } from '@/config/contracts-generated'
import { EVC_ADDRESS } from '@/config/euler-contracts'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { SupportedChainId } from '@/domain/types/SupportedChainId'
import { contractBook } from '@eulerxyz/core-sdk'
import { mapToEVCCall } from '@eulerxyz/evc-sdk'
import {
  type Asset,
  type EnableCollateralTransactionItem,
  GeneralTransactionFactoryUnifier,
  type TransactionItems,
  TransactionLabel,
  type UnwrapTransactionItem,
  type WrapTransactionItem,
} from '@eulerxyz/wagmi-evc'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { Address } from 'viem'

// =============================================================================
// Types
// =============================================================================

export interface EvcNativeDepositParams {
  vault: Address
  amount: bigint
  account: Address
  receiver: Address
  chainId: number
  totalAssets?: bigint
  totalShares?: bigint
}

export interface EvcNativeWithdrawParams {
  vault: Address
  amount: bigint
  account: Address
  receiver: Address
  chainId: number
  isMax?: boolean
  /** User's vault shares (for isMax=true to avoid dust) */
  userShares?: string
}

// =============================================================================
// Transaction Item Creators
// =============================================================================

function getWhypeAsset(chainId: number): Asset {
  const whypeAddress = getContractAddress(whypeConfig.address, chainId)
  return {
    address: whypeAddress,
    name: 'Wrapped Hype',
    symbol: 'WHYPE',
    decimals: 18,
  }
}

/**
 * Creates a WRAP transaction item for native HYPE deposit.
 */
export function createEvcWrapTransactionItem({
  vault,
  amount,
  account,
  receiver,
  chainId,
  totalAssets,
  totalShares,
}: EvcNativeDepositParams): WrapTransactionItem {
  return {
    label: TransactionLabel.WRAP,
    asset: getWhypeAsset(chainId),
    from: account,
    to: receiver,
    value: amount.toString(),
    vault,
    totalAssets: totalAssets?.toString(),
    totalShares: totalShares?.toString(),
  }
}

/**
 * Creates an UNWRAP transaction item for native HYPE withdrawal.
 * When isMax is true, the factory adds an extra redeem call with MaxUint256 to avoid dust.
 */
export function createEvcUnwrapTransactionItem({
  vault,
  amount,
  account,
  receiver,
  chainId,
  isMax = false,
  userShares,
}: EvcNativeWithdrawParams): UnwrapTransactionItem {
  return {
    label: TransactionLabel.UNWRAP,
    asset: getWhypeAsset(chainId),
    from: account,
    to: receiver,
    value: amount.toString(),
    vault,
    isMax,
    userShares, // Pass userShares for precise dust handling
  } as UnwrapTransactionItem
}

// =============================================================================
// Batch Call Generation
// =============================================================================

/**
 * Converts transaction items to EVC batch call config using wagmi-evc's factory.
 * This uses the same code path as Euler's production app.
 */
export function createEvcBatchConfig({
  transactions,
  chainId,
  value = 0n,
}: {
  transactions: TransactionItems
  chainId: SupportedChainId
  value?: bigint
}) {
  const factory = new GeneralTransactionFactoryUnifier(chainId as any, contractBook)

  // Convert transaction items to ContractFunctionConfig | EVCCall format
  const convertedCalls = factory.typeGuardConvertMultiple(transactions)

  // Map to EVCCalls format (what the EVC.batch function expects)
  const batchItems = mapToEVCCall(convertedCalls)

  const evcAddress = EVC_ADDRESS

  return {
    abi: eulerVaultConnectorAbi,
    address: evcAddress,
    functionName: 'batch' as const,
    chainId,
    value,
    args: [batchItems] as const,
  }
}

/**
 * Creates EVC batch config for native HYPE deposit.
 * Uses wagmi-evc's WETHTransactionFactory under the hood.
 */
export function createNativeDepositBatchConfig(params: EvcNativeDepositParams) {
  const wrapItem = createEvcWrapTransactionItem(params)

  return createEvcBatchConfig({
    transactions: [wrapItem],
    chainId: params.chainId as SupportedChainId,
    value: params.amount, // Send native HYPE as value
  })
}

export function createNativeCollateralDepositBatchConfig(params: EvcNativeDepositParams) {
  const wrapItem = createEvcWrapTransactionItem(params)
  const enableCollateralItem: EnableCollateralTransactionItem = {
    label: TransactionLabel.ENABLE_COLLATERAL,
    vault: params.vault,
    asset: getWhypeAsset(params.chainId),
    from: params.receiver,
    to: params.receiver,
  }

  return createEvcBatchConfig({
    transactions: [wrapItem, enableCollateralItem],
    chainId: params.chainId as SupportedChainId,
    value: params.amount, // Send native HYPE as value
  })
}

/**
 * Creates EVC batch config for native HYPE withdrawal (unwrap).
 * Uses wagmi-evc's WETHTransactionFactory under the hood.
 */
export function createNativeWithdrawBatchConfig(params: EvcNativeWithdrawParams) {
  const unwrapItem = createEvcUnwrapTransactionItem(params)

  return createEvcBatchConfig({
    transactions: [unwrapItem],
    chainId: params.chainId as SupportedChainId,
    value: 0n, // No native value for withdrawals
  })
}
